import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Play, Clock } from "lucide-react";

export default function Subject() {
  const { slug } = useParams();
  const [subject, setSubject] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [completedIds, setCompletedIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      const [subs, lessResp, progResp] = await Promise.all([
        api.get("/curriculum/subjects"),
        api.get(`/curriculum/lessons?subject=${slug}`),
        api.get("/progress").catch(() => ({ data: [] })),
      ]);
      setSubject(subs.data.find((s) => s.slug === slug));
      setLessons(lessResp.data);
      setCompletedIds(new Set(progResp.data.filter((p) => p.status === "completed").map((p) => p.lesson_id)));
    })();
  }, [slug]);

  if (!subject) return <div className="text-slate-500">Loading…</div>;

  // Group by module
  const modules = {};
  for (const l of lessons) { (modules[l.module] ||= []).push(l); }

  return (
    <div className="space-y-8" data-testid={`subject-page-${slug}`}>
      <div>
        <div className="chip mb-2">{subject.tagline}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{subject.title}</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">{subject.description}</p>
      </div>

      {Object.entries(modules).map(([mod, list]) => (
        <section key={mod}>
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">{mod}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {list.map((l) => {
              const done = completedIds.has(l.id);
              return (
                <Link key={l.id} to={`/lessons/${l.id}`} data-testid={`lesson-card-${l.id}`}
                  className="p-6 rounded-3xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> ~{l.minutes} min</span>
                    {done ? <span className="chip"><CheckCircle2 size={12} /> Done</span> : <span className="text-teal-700 text-sm font-semibold flex items-center gap-1"><Play size={12} /> Start</span>}
                  </div>
                  <div className="font-display text-lg font-semibold text-slate-900">{l.title}</div>
                  <p className="text-sm text-slate-600 line-clamp-2">{l.summary}</p>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
